import { AddCircleOutlineOutlined } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import { Box, Grid, IconButton, MenuItem } from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import React from "react";
import * as Yup from "yup";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import { TransformedData } from "../associated parcelcenters/AssociatedParcelCenterGrid";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { FdpResponse } from "../../../app/models/responseModels/FdpResponse";
import { addClientPcFdpMapping } from "../../../app/features/client/clientSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";

interface AddParcelCenterMappingModalProps {
  clientId: number;
  data: TransformedData[];
  alreadyMappedPcs: number[];
  closeModal: CallableFunction;
  onCompleted?: CallableFunction;
}

type ClientParcelCenterFdpMapping = {
  parcelCenterId: string;
  fdpIds: number[];
};

export type InitialValues = {
  clientId: number;
  clientParcelCenterFdpMappings: ClientParcelCenterFdpMapping[];
  createdBy: string;
  updatedBy: string;
};

const AddParcelCenterMappingModal: React.FC<
  AddParcelCenterMappingModalProps
> = ({ clientId, data, alreadyMappedPcs, closeModal, onCompleted }) => {
  const { parcelCenterList } = useSelector(
    (state: RootState) => state.parcelCenter
  );

  const { fdpList } = useSelector((state: RootState) => state.fdp);

  const { authResponse } = useSelector((state: RootState) => state.login);

  const dispatch = useAppDispatch();

  const validationSchema = Yup.object().shape({
    clientParcelCenterFdpMappings: Yup.array().of(
      Yup.object().shape({
        parcelCenterId: Yup.number().required(
          "Parcel Center selection is required"
        ),
        fdpIds: Yup.array()
          .of(Yup.number())
          .required("FDP selection is required")
          .min(1, "Pick at least one fdp"),
      })
    ),
  });

  const initialValues: InitialValues = {
    clientId: clientId,
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
    clientParcelCenterFdpMappings: [
      {
        fdpIds: [],
        parcelCenterId: "",
      },
    ],
  };

  const handleSubmit = async (
    values: InitialValues,
    { setFieldError, resetForm }: any
  ) => {
    try {
      const response = await dispatch(addClientPcFdpMapping(values)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, { toastId: "addClientPcFdpSuccess" });
        onCompleted && onCompleted(values);
      } else {
        toast.error(response.message!, { toastId: "addClientPcFdpError" });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addClientPcFdpException",
      });
    } finally {
      resetForm();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, setFieldError, resetForm }) => {
        setSubmitting(true);
        handleSubmit(values, { setFieldError, resetForm }).finally(() =>
          setSubmitting(false)
        );
      }}
    >
      {({ values, isSubmitting, setFieldValue }) => {
        return (
          <SubmitModalWithFormik
            title="Add Client to Parcel Center"
            closeSubmitModal={closeModal}
            allowSubmit={true}
            size="lg"
            isLoading={isSubmitting}
            submitButtonName="Add"
            closeModalAfterSubmit={false}
            key="submitModal"
          >
            <Form>
              <FieldArray name="clientParcelCenterFdpMappings">
                {({ push, remove }) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {values.clientParcelCenterFdpMappings.map((_, index) => (
                      <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={2}
                        justifyContent="center"
                        alignItems="center"
                        key={index}
                      >
                        <Grid item xs={12} sm={5} md={5} lg={5}>
                          <FormikTextField
                            select
                            margin="normal"
                            id={`clientParcelCenterFdpMappings.${index}.parcelCenterId`}
                            name={`clientParcelCenterFdpMappings.${index}.parcelCenterId`}
                            label="Parcel Center"
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: { style: { maxHeight: 150 } },
                              },
                            }}
                            required
                          >
                            {parcelCenterList
                              .filter(
                                (e) =>
                                  !alreadyMappedPcs.includes(
                                    parseInt(e.parcelCenterId)
                                  )
                              )
                              .filter(
                                (e) =>
                                  !values.clientParcelCenterFdpMappings.some(
                                    (mapping, mappingIndex) =>
                                      mapping.parcelCenterId ===
                                        e.parcelCenterId &&
                                      mappingIndex !== index
                                  )
                              )
                              .map((e) => (
                                <MenuItem
                                  key={e.parcelCenterId}
                                  value={e.parcelCenterId}
                                >
                                  {e.parcelCenterName}
                                </MenuItem>
                              ))}
                          </FormikTextField>
                        </Grid>
                        <Grid item xs={12} sm={5} md={5} lg={5}>
                          <FormikTextField
                            select
                            margin="normal"
                            id={`clientParcelCenterFdpMappings.${index}.fdpIds`}
                            name={`clientParcelCenterFdpMappings.${index}.fdpIds`}
                            label="FDP(s)"
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: { style: { maxHeight: 150 } },
                              },
                              multiple: true,
                            }}
                            required
                            isDisabled={
                              values.clientParcelCenterFdpMappings[index]
                                .parcelCenterId
                                ? false
                                : true
                            }
                          >
                            {(
                              parcelCenterList.find(
                                (e) =>
                                  e.parcelCenterId ==
                                  values.clientParcelCenterFdpMappings[index]
                                    .parcelCenterId
                              )?.fdps || ([] as FdpResponse[])
                            ).map((e, i) => (
                              <MenuItem
                                key={(e.id + i).toString()}
                                value={e.id}
                              >
                                {e.companyName}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                        <Grid item xs={12} sm={2} md={2} lg={2}>
                          <IconButton
                            onClick={() =>
                              push({
                                parcelCenterId: "",
                                fdpIds: [],
                              })
                            }
                          >
                            <AddCircleOutlineOutlined />
                          </IconButton>
                          {values.clientParcelCenterFdpMappings.length > 1 && (
                            <IconButton
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                )}
              </FieldArray>
            </Form>
          </SubmitModalWithFormik>
        );
      }}
    </Formik>
  );
};

export default AddParcelCenterMappingModal;
